import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const DISPLAY_DATE_FORMAT = "DD/MM/YYYY";
export const DISPLAY_DATE_TIME_FORMAT = "DD/MM/YYYY HH:mm:ss";
export const DISPLAY_DATE_TIME_MINUTE_FORMAT = "DD/MM/YYYY HH:mm";
export const API_DATE_FORMAT = "YYYY-MM-DD";
export const API_DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ss";

const TRY_PARSE_FORMATS = [
  API_DATE_TIME_FORMAT,
  "YYYY-MM-DD HH:mm:ss",
  API_DATE_FORMAT,
  DISPLAY_DATE_TIME_FORMAT,
  DISPLAY_DATE_TIME_MINUTE_FORMAT,
  DISPLAY_DATE_FORMAT,
];

const dateTime = ["siCutOffTime", "cyCutOffTime"];
const date = ["createdDate", "eta", "etd"];
export const FormatDateFields = (value: any, key?: string): any => {
  if (value === null || value === undefined) return value;
  const formatType = dateTime.includes(key || "") ? API_DATE_TIME_FORMAT : date.includes(key || "") ? API_DATE_FORMAT : null;
  // Nếu không nằm trong dateTime[] và date[] thì giữ nguyên
  if (!formatType) {
    if (Array.isArray(value)) {
      return value.map((v) => FormatDateFields(v));
    }

    if (typeof value === "object") {
      const out: any = {};
      for (const k of Object.keys(value)) {
        out[k] = FormatDateFields(value[k], k);
      }
      return out;
    }
    return value;
  }

  // Nếu là Dayjs object
  if (typeof value === "object" && typeof value.toDate === "function") {
    try {
      const converted = value.toDate();
      if (converted instanceof Date && !isNaN(converted.getTime())) {
        return dayjs(converted).format(formatType);
      }
    } catch {}
  }

  // Nếu là Date object
  if (value instanceof Date) {
    return !isNaN(value.getTime()) ? dayjs(value).format(formatType) : value;
  }

  // Nếu là chuỗi parse được
  if (typeof value === "string") {
    const parsed = dayjs(value);
    if (parsed.isValid()) {
      return parsed.format(formatType);
    }
  }
  return value;
};

export const convertToDayjs = (item?: string | number | Date | Dayjs | null): Dayjs | undefined => {
  if (item === null || item === undefined || item === "") return undefined;
  if (typeof item !== "string") {
    const d = dayjs(item);
    return d.isValid() ? d : undefined;
  }
  for (const f of TRY_PARSE_FORMATS) {
    const d = dayjs(item, f, true);
    if (d.isValid()) return d;
  }
  const def = dayjs(item);
  if (def.isValid()) return def;
  return undefined;
};
export const formatDate = (item?: string | number | Date | Dayjs | null): string => {
  return convertToDayjs(item)?.format(DISPLAY_DATE_FORMAT) ?? "";
};

export const formatDateTime = (item?: string | number | Date | Dayjs | null): string => {
  return convertToDayjs(item)?.format(DISPLAY_DATE_TIME_FORMAT) ?? "";
};

export type SerializeOptions = {
  /**
   * Danh sách tên property (exact) luôn serialize như DATETIME (even if time === 00:00:00).
   * Ví dụ: ["siCutOffTime", "cyCutOffTime"]
   */
  forceDateTimeKeys?: string[];

  /**
   * Nếu true -> trả chuỗi local dạng 'YYYY-MM-DDTHH:mm:ss' (NO Z).
   * Nếu false -> trả ISO UTC via toISOString() (có Z).
   */
  preserveLocalTime?: boolean;

  /**
   * Nếu true -> sẽ tự động coi những key khớp regex datetimeKeyRegex là datetime.
   */
  autoDetectDateTimeKeys?: boolean;

  /**
   * Regex dùng để tự động phát hiện tên key có khả năng là datetime.
   * Mặc định detect những key chứa 'cutoff', 'cut_off', 'cutOff', 'time', 'eta', 'etd'.
   */
  datetimeKeyRegex?: RegExp;
};

const DEFAULT_OPTIONS: SerializeOptions = {
  forceDateTimeKeys: [],
  preserveLocalTime: true,
  autoDetectDateTimeKeys: false,
};

function formatDay(d: Dayjs, asDateTime: boolean, preserveLocalTime: boolean) {
  if (asDateTime) {
    return preserveLocalTime ? d.format(API_DATE_TIME_FORMAT) : d.toISOString();
  }
  // date-only
  return d.format(API_DATE_FORMAT);
}

/**
 * Deep-serialize object containing dayjs/Date/ISO strings into either date-only 'YYYY-MM-DD'
 * or datetime 'YYYY-MM-DDTHH:mm:ss' or ISO Z depending rules/options.
 */
export function serializeDayjsDeep(value: any, keyPath: string[] = [], opts?: Partial<SerializeOptions>): any {
  const options: SerializeOptions = { ...DEFAULT_OPTIONS, ...(opts || {}) };
  const currentKey = keyPath.length ? keyPath[keyPath.length - 1] : undefined;

  const isForcedKey = !!currentKey && options.forceDateTimeKeys!.includes(currentKey as string);
  const isAutoDetectedKey = !!currentKey && options.autoDetectDateTimeKeys && options.datetimeKeyRegex!.test(currentKey as string);

  const decideAsDateTime = (d: Dayjs) => {
    const hasTime = d.hour() !== 0 || d.minute() !== 0 || d.second() !== 0 || d.millisecond() !== 0;
    return hasTime || isForcedKey || isAutoDetectedKey;
  };

  if (value === null || value === undefined) return value;

  if (dayjs.isDayjs(value)) {
    const asDateTime = decideAsDateTime(value);
    return formatDay(value, asDateTime as any, options.preserveLocalTime!);
  }

  if (value instanceof Date) {
    const d = dayjs(value);
    const asDateTime = decideAsDateTime(d);
    return formatDay(d, asDateTime as any, options.preserveLocalTime!);
  }

  if (value && typeof value === "object" && value.$isDayjsObject) {
    const d = dayjs(value);
    const asDateTime = decideAsDateTime(d);
    return formatDay(d, asDateTime as any, options.preserveLocalTime!);
  }

  if (typeof value === "string") {
    const isoLike = /^\d{4}-\d{2}-\d{2}([T\s]\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+\-]\d{2}:\d{2})?)?$/.test(value);
    const slashLike = /^\d{2}\/\d{2}\/\d{4}(\s\d{2}:\d{2}(:\d{2})?)?$/.test(value);
    if (isoLike || slashLike) {
      const d = convertToDayjs(value);
      if (d?.isValid()) {
        const asDateTime = decideAsDateTime(d);
        return formatDay(d, asDateTime as any, options.preserveLocalTime!);
      }
    }
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((v, i) => serializeDayjsDeep(v, [...keyPath, String(i)], options));
  }

  if (typeof value === "object") {
    const out: any = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = serializeDayjsDeep(v, [...keyPath, k], options);
    }
    return out;
  }

  return value;
}

export const isWithin24Hours = (input: string | null | undefined): boolean => {
  if (!input) {
    console.warn("input is null or undefined");
    return false; // hoặc return true tùy logic bạn muốn
  }

  // Cắt microseconds (JS không parse được 6 chữ số)
  const normalized = input.replace(/\.(\d{3})\d*/, ".$1");

  const inputDate = new Date(normalized);

  if (isNaN(inputDate.getTime())) {
    console.warn("Invalid date:", normalized);
    return false;
  }

  const now = new Date();
  const diffMs = now.getTime() - inputDate.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  return diffHours <= 24;
};

const formatTimeZoneHcm = (data: string): Dayjs | undefined => {
  return data ? dayjs(data).tz("Asia/Ho_Chi_Minh") : undefined;
};
export const formatTimeZoneHcmToString = (data: string): string | undefined => {
  return formatTimeZoneHcm(data)?.format(DISPLAY_DATE_FORMAT);
};

export const formatDateFields = (values: any, formConfig: any, colType: any) => {
  if (!formConfig) return values;
  const result = { ...values };
  formConfig.userFormColumnDetailDtos.forEach((col: any) => {
    const name = col.colName;
    if (!result.hasOwnProperty(name)) return;

    const val = result[name];
    if (!val) return;

    if (col.colDataTypeId === colType.DATE) {
      if (val && typeof val.format === "function") {
        result[name] = formatTimeZoneHcmToString(val);
      }
    } else if (col.colDataTypeId === colType.DATETIME) {
      if (val && typeof val.format === "function") {
        result[name] = val.format(DISPLAY_DATE_TIME_FORMAT);
      }
    }
  });
  return result;
};

// API/localStorage → Form
export const convertDateStringsToDayjsDeep = (values: any, keyPath: string[] = []): any => {
  if (values === null || values === undefined) return values;

  const currentKey = keyPath.length ? keyPath[keyPath.length - 1] : "";
  const isDateKey = /(date|time|deadline|cutoff|cut_off|eta|etd)/i.test(currentKey);
  const dateLikeRegex = /^\d{4}-\d{2}-\d{2}([T\s]\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+\-]\d{2}:\d{2})?)?$/;

  if (dayjs.isDayjs(values)) return values;

  if (values instanceof Date) {
    const d = dayjs(values);
    return d.isValid() ? d : values;
  }

  if (typeof values === "string") {
    if (isDateKey && dateLikeRegex.test(values)) {
      const d = dayjs(values);
      return d.isValid() ? d : values;
    }
    return values;
  }

  if (Array.isArray(values)) {
    return values.map((value, index) => convertDateStringsToDayjsDeep(value, [...keyPath, String(index)]));
  }

  if (typeof values === "object") {
    if (values.$isDayjsObject || values.$d) {
      const d = dayjs(values.$d ?? values);
      if (d.isValid()) return d;
    }

    const processed: any = {};
    Object.keys(values).forEach((key) => {
      processed[key] = convertDateStringsToDayjsDeep(values[key], [...keyPath, key]);
    });
    return processed;
  }

  return values;
};
